import { useState, useEffect } from 'react';
import { Button, Table, Row, Col, Card, Form, InputGroup, Accordion, Badge, Modal } from 'react-bootstrap';
import { FaCheck, FaEdit, FaTrash, FaSearch, FaUsers } from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';

import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Paginate from '../../components/ui/Paginate';
import Meta from '../../components/layout/Meta';
import { useDeleteUserMutation, useGetUsersQuery } from '../../slices/userApiSlice';
import useIsMobile from '../../utils/useIsMobile';
import UserEditScreen from './UserEditScreen';

const UserListScreen = () => {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { pageNumber = 1, keyword: urlKeyword = '' } = useParams();

    const [keyword, setKeyword] = useState(urlKeyword);
    const [isAdminFilter, setIsAdminFilter] = useState('');
    const [isClientFilter, setIsClientFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const {
        data,
        isLoading: loadingUsers,
        refetch: refetchUsers,
        error: errorUsers,
    } = useGetUsersQuery({
        pageNumber,
        keyword: urlKeyword,
        isAdmin: isAdminFilter,
        isClient: isClientFilter,
        type: typeFilter,
    });

    const users = data?.users;
    const pages = data?.pages;
    const page = data?.page;

    const [deleteUser, { isLoading: loadingDelete }] = useDeleteUserMutation();

    useEffect(() => {
        setKeyword(urlKeyword);
    }, [urlKeyword]);

    const deleteHandler = async (id) => {
        if (window.confirm('Esta seguro de elminar el usuario?')) {
            try {
                await deleteUser(id);
                refetchUsers();
            } catch (err) {
                toast.error(err?.data?.message || err.error);
            }
        }
    };

    const submitHandler = (e) => {
        e.preventDefault();
        if (keyword.trim()) {
            navigate(`/admin/userlist/search/${keyword.trim()}`);
        } else {
            navigate('/admin/userlist');
        }
    };

    const clearSearch = () => {
        setKeyword('');
        navigate('/admin/userlist');
    };

    const openEditModal = (userId) => {
        setSelectedUserId(userId);
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setSelectedUserId(null);
    };

    return (
        <>
            <Meta title="Lista de Usuarios" />
            <div className="admin-header">
                <h1 className="admin-title responsive-title">
                    <FaUsers className="title-icon" /> Usuarios
                    {data?.total && (
                        <Badge bg="secondary" className="ms-2">
                            {data.total}
                        </Badge>
                    )}
                </h1>
            </div>

            <Card className="mb-4 shadow-sm border-0">
                <Card.Body className="p-3">
                    <Form onSubmit={submitHandler}>
                        <Row className="gy-3 align-items-center">
                            <Col xs={12} lg={6}>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar por nombre, email, doc..."
                                        value={keyword}
                                        onChange={(e) => setKeyword(e.target.value)}
                                    />
                                    {keyword && (
                                        <Button variant="outline-secondary" onClick={clearSearch}>
                                            <FaX />
                                        </Button>
                                    )}
                                    <Button type="submit" variant="primary">
                                        <FaSearch />
                                    </Button>
                                </InputGroup>
                            </Col>
                            <Col xs={12} lg={6}>
                                <Accordion flush>
                                    <Accordion.Item eventKey="0" className="border-0">
                                        <Accordion.Header className="bg-transparent border-0 p-0">
                                            <span className="text-primary small fw-bold">Filtros Avanzados</span>
                                        </Accordion.Header>
                                        <Accordion.Body className="px-0 py-2">
                                            <Row className="g-2">
                                                <Col xs={6} md={4}>
                                                    <Form.Select
                                                        size="sm"
                                                        value={isAdminFilter}
                                                        onChange={(e) => setIsAdminFilter(e.target.value)}
                                                    >
                                                        <option value="">Admin: Todos</option>
                                                        <option value="true">Sí</option>
                                                        <option value="false">No</option>
                                                    </Form.Select>
                                                </Col>
                                                <Col xs={6} md={4}>
                                                    <Form.Select
                                                        size="sm"
                                                        value={isClientFilter}
                                                        onChange={(e) => setIsClientFilter(e.target.value)}
                                                    >
                                                        <option value="">Cliente: Todos</option>
                                                        <option value="true">Sí</option>
                                                        <option value="false">No</option>
                                                    </Form.Select>
                                                </Col>
                                                <Col xs={12} md={4}>
                                                    <Form.Select
                                                        size="sm"
                                                        value={typeFilter}
                                                        onChange={(e) => setTypeFilter(e.target.value)}
                                                    >
                                                        <option value="">Tipo: Todos</option>
                                                        <option value="C">Consumidor (C)</option>
                                                        <option value="J">Jurídico (J)</option>
                                                    </Form.Select>
                                                </Col>
                                            </Row>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {loadingDelete && <Loader />}
            {loadingUsers ? (
                <Loader />
            ) : errorUsers ? (
                <Message variant="danger">
                    {errorUsers?.data?.message ||
                        errorUsers?.error ||
                        'Ocurrió un error inesperado'}
                </Message>
            ) : (
                <>
                    {!isMobile && (
                        <div className="admin-table-container shadow-sm mb-4">
                            <Table hover responsive className="admin-table m-0">
                                <thead>
                                    <tr>
                                        <th>CED/RUC</th>
                                        <th className="text-start">NOMBRE</th>
                                        <th className="text-start">EMAIL</th>
                                        <th>ADMIN</th>
                                        <th>CLIENTE</th>
                                        <th>EXTRANJERO</th>
                                        <th>PRECIO</th>
                                        <th className="text-center">ACCIONES</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users?.map((user) => (
                                        <tr key={user._id}>
                                            <td><Badge bg="light" text="dark">{user.taxDocId ? user.taxDocId : user.docId}</Badge></td>
                                            <td className="text-start fw-bold">{user.name || 'user'}</td>
                                            <td className="text-start">
                                                <a href={`mailto:${user.email}`} className="text-decoration-none">
                                                    {user.email}
                                                </a>
                                            </td>
                                            <td>
                                                {user.isAdmin ? <FaCheck className="text-success" /> : <FaX className="text-danger" />}
                                            </td>
                                            <td>
                                                {user.isClient ? (
                                                    <FaCheck className="text-success" />
                                                ) : (
                                                    <FaX className="text-danger" />
                                                )}
                                            </td>
                                            <td>
                                                {user.isForeigner ? (
                                                    <FaCheck className="text-success" />
                                                ) : (
                                                    <FaX className="text-danger" />
                                                )}
                                            </td>
                                            <td>
                                                <Badge
                                                    style={{
                                                        backgroundColor: '#8f320b',
                                                        color: '#ffffff',
                                                        border: '1px solid #b5430f',
                                                    }}
                                                >
                                                    {user.priceAssigned ? user.priceAssigned : 'N/A'}
                                                </Badge>
                                            </td>
                                            <td className="text-center">
                                                <div className="action-buttons justify-content-center">
                                                    <Button
                                                        variant="outline-primary"
                                                        className="btn-sm"
                                                        title="Editar"
                                                        onClick={() => openEditModal(user._id)}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        className="btn-sm"
                                                        onClick={() => deleteHandler(user._id)}
                                                        title="Eliminar"
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {isMobile && (
                        <div className="user-cards-container">
                            {users?.map((user) => (
                                <Card key={user._id} className="mb-3 shadow-sm border-0">
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                                <div className="fw-bold fs-5">{user.name || 'user'}</div>
                                                <div className="text-muted small">{user.taxDocId || user.docId}</div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="light"
                                                    className="btn-sm border"
                                                    onClick={() => openEditModal(user._id)}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    variant="light"
                                                    className="btn-sm border text-danger"
                                                    onClick={() => deleteHandler(user._id)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </div>
                                        </div>
                                        
                                        <div className="mb-2">
                                            <a href={`mailto:${user.email}`} className="text-decoration-none small">
                                                {user.email}
                                            </a>
                                        </div>

                                        <Row className="g-2 text-center small">
                                            <Col xs={4}>
                                                <div className="bg-light p-1 rounded">
                                                    <div className="text-muted x-small">Admin</div>
                                                    {user.isAdmin ? <FaCheck color="green" /> : <FaX color="red" />}
                                                </div>
                                            </Col>
                                            <Col xs={4}>
                                                <div className="bg-light p-1 rounded">
                                                    <div className="text-muted x-small">Cliente</div>
                                                    {user.isClient ? <FaCheck color="green" /> : <FaX color="red" />}
                                                </div>
                                            </Col>
                                            <Col xs={4}>
                                                <div className="bg-light p-1 rounded">
                                                    <div className="text-muted x-small">Extranj.</div>
                                                    {user.isForeigner ? <FaCheck color="green" /> : <FaX color="red" />}
                                                </div>
                                            </Col>
                                        </Row>
                                        <div className="mt-2 text-center small bg-light p-1 rounded">
                                            <span className="text-muted">Precio:</span> {user.priceAssigned || 'N/A'}
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}

                    <Paginate
                        pages={pages}
                        page={page}
                        isAdmin="users"
                        keyword={urlKeyword}
                    />
                </>
            )}

            <Modal
                show={showEditModal}
                onHide={closeEditModal}
                size="lg"
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Editar Usuario</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedUserId && (
                        <UserEditScreen
                            userId={selectedUserId}
                            embedded
                            onClose={closeEditModal}
                            onSaved={() => refetchUsers()}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default UserListScreen;
